import React, { useState } from 'react';
import { fctrApi } from '../services/api';
import '../styles/Modal.css';

/**
 * Modal for generating FCTR (Foreign Currency Translation Reserve) calculation
 *
 * Features:
 * - Date range selection (flexible periods: monthly, quarterly, half-yearly, annual)
 * - Displays entity's base and consolidation currencies
 * - Loading state during generation
 * - Success/error messaging with download link
 */
function GenerateFCTRModal({ entity, onClose, onComplete }) {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [result, setResult] = useState(null);

  // Manual FX rates state
  const [useManualRates, setUseManualRates] = useState(false);
  const [historicalRate, setHistoricalRate] = useState('');
  const [closingRate, setClosingRate] = useState('');
  const [averageRate, setAverageRate] = useState('');

  const handleGenerate = async () => {
    // Validation
    if (!startDate || !endDate) {
      setError('Please select both start date and end date');
      return;
    }

    // Validate date order
    if (new Date(startDate) >= new Date(endDate)) {
      setError('Start date must be before end date');
      return;
    }

    // Validate manual rates if enabled
    if (useManualRates) {
      if (!historicalRate || !closingRate || !averageRate) {
        setError('Please enter all three exchange rates (Historical, Closing, Average)');
        return;
      }

      const rates = [
        parseFloat(historicalRate),
        parseFloat(closingRate),
        parseFloat(averageRate)
      ];

      if (rates.some(r => isNaN(r) || r <= 0)) {
        setError('Please enter valid positive numbers for all exchange rates');
        return;
      }
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(false);

      // Prepare manual rates if enabled
      let manualRates = null;
      if (useManualRates) {
        manualRates = {
          historical_rate: parseFloat(historicalRate),
          closing_rate: parseFloat(closingRate),
          average_rate: parseFloat(averageRate),
        };
      }

      // Call API to generate FCTR
      const response = await fctrApi.generateFCTR(entity.name, startDate, endDate, manualRates);

      if (response.success) {
        setSuccess(true);
        setResult(response);

        // Notify parent component
        if (onComplete) {
          onComplete(response);
        }
      } else {
        setError(response.error || 'FCTR generation failed');
      }
    } catch (err) {
      console.error('Error generating FCTR:', err);
      setError(
        err.data?.error ||
        err.message ||
        'Failed to generate FCTR. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return 'N/A';
    return parseFloat(amount).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const baseCurrency = entity.fx_rates?.base_currency || 'N/A';
  const consolidationCurrency = entity.fx_rates?.consolidation_currency || 'N/A';

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Generate FCTR Working</h2>
          <button
            className="modal-close"
            onClick={handleClose}
            disabled={loading}
            aria-label="Close modal"
          >
            ×
          </button>
        </div>

        <div className="modal-body">
          {/* Entity Info */}
          <div className="info-box" style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: '#F3F4F6', borderRadius: '4px' }}>
            <p style={{ margin: '0.25rem 0', fontSize: '0.95rem' }}>
              <strong>Entity:</strong> {entity.description || entity.name}
            </p>
            <p style={{ margin: '0.25rem 0', fontSize: '0.95rem' }}>
              <strong>Base Currency:</strong> {baseCurrency}
            </p>
            <p style={{ margin: '0.25rem 0', fontSize: '0.95rem' }}>
              <strong>Consolidation Currency:</strong> {consolidationCurrency}
            </p>
          </div>

          {/* Date Selection */}
          <div className="form-section">
            <h4 style={{ marginBottom: '1rem', fontSize: '1rem', color: '#374151' }}>
              Select Period
            </h4>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div className="form-group">
                <label htmlFor="startDate">Start Date</label>
                <input
                  id="startDate"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  disabled={loading || success}
                  className="form-control"
                />
              </div>

              <div className="form-group">
                <label htmlFor="endDate">End Date</label>
                <input
                  id="endDate"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  disabled={loading || success}
                  className="form-control"
                />
              </div>
            </div>

            <p style={{ fontSize: '0.875rem', color: '#6B7280', marginTop: '0.5rem' }}>
              You can select monthly, quarterly, half-yearly, or annual periods.
            </p>
          </div>

          {/* Exchange Rates Section */}
          <div className="form-section" style={{ marginTop: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
              <h4 style={{ margin: 0, fontSize: '1rem', color: '#374151' }}>
                Exchange Rates
              </h4>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '0.875rem' }}>
                <input
                  type="checkbox"
                  checked={useManualRates}
                  onChange={(e) => setUseManualRates(e.target.checked)}
                  disabled={loading || success}
                  style={{ marginRight: '0.5rem' }}
                />
                Enter rates manually
              </label>
            </div>

            {!useManualRates ? (
              <div className="alert alert-info" style={{ fontSize: '0.875rem' }}>
                <p style={{ margin: 0 }}>
                  📊 Exchange rates will be fetched automatically from SAP for the selected period.
                </p>
              </div>
            ) : (
              <div style={{ padding: '1rem', backgroundColor: '#F9FAFB', borderRadius: '4px', border: '1px solid #E5E7EB' }}>
                <p style={{ fontSize: '0.875rem', color: '#6B7280', marginBottom: '1rem' }}>
                  Enter exchange rates for <strong>{baseCurrency}</strong> → <strong>{consolidationCurrency}</strong>
                </p>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
                  <div className="form-group">
                    <label htmlFor="historicalRate" style={{ fontSize: '0.875rem' }}>
                      Historical Rate (Opening)
                    </label>
                    <input
                      id="historicalRate"
                      type="number"
                      step="0.000001"
                      value={historicalRate}
                      onChange={(e) => setHistoricalRate(e.target.value)}
                      disabled={loading || success}
                      className="form-control"
                      placeholder="e.g., 1.234567"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="closingRate" style={{ fontSize: '0.875rem' }}>
                      Closing Rate
                    </label>
                    <input
                      id="closingRate"
                      type="number"
                      step="0.000001"
                      value={closingRate}
                      onChange={(e) => setClosingRate(e.target.value)}
                      disabled={loading || success}
                      className="form-control"
                      placeholder="e.g., 1.256789"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="averageRate" style={{ fontSize: '0.875rem' }}>
                      Average Rate
                    </label>
                    <input
                      id="averageRate"
                      type="number"
                      step="0.000001"
                      value={averageRate}
                      onChange={(e) => setAverageRate(e.target.value)}
                      disabled={loading || success}
                      className="form-control"
                      placeholder="e.g., 1.245678"
                    />
                  </div>
                </div>

                <p style={{ fontSize: '0.75rem', color: '#6B7280', marginTop: '0.75rem', marginBottom: 0 }}>
                  💡 <strong>Tip:</strong> Historical rate is used for non-monetary items and equity,
                  closing rate for monetary items, and average rate for income statement items.
                </p>
              </div>
            )}
          </div>

          {/* IndAS 21 Information */}
          <div className="alert alert-info" style={{ marginTop: '1.5rem', fontSize: '0.875rem' }}>
            <strong>IndAS 21 & Schedule III:</strong>
            <p style={{ marginTop: '0.5rem', marginBottom: 0 }}>
              FCTR calculation translates foreign operations from <strong>{baseCurrency}</strong> to{' '}
              <strong>{consolidationCurrency}</strong>. Monetary items use closing rate, non-monetary
              items use historical rate, and translation differences are accumulated in Other Equity - FCTR
              as per Schedule III.
            </p>
          </div>

          {/* Error Display */}
          {error && (
            <div className="alert alert-error" style={{ marginTop: '1rem' }}>
              {error}
            </div>
          )}

          {/* Success Display */}
          {success && result && (
            <div className="alert alert-success" style={{ marginTop: '1rem' }}>
              <h4 style={{ marginBottom: '0.5rem' }}>FCTR Calculation Completed Successfully!</h4>
              <p style={{ marginBottom: '0.5rem' }}>
                <strong>FCTR Movement:</strong>{' '}
                {consolidationCurrency} {formatCurrency(result.fctr_movement)}
              </p>
              {result.fx_rates && (
                <div style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>
                  <p style={{ margin: '0.25rem 0', color: '#6B7280' }}>
                    <em>{useManualRates ? '📝 Manual rates' : '📊 Rates from SAP'}:</em>
                  </p>
                  <p style={{ margin: '0.25rem 0' }}>
                    Historical Rate: {result.fx_rates.historical_rate?.toFixed(6)}
                  </p>
                  <p style={{ margin: '0.25rem 0' }}>
                    Closing Rate: {result.fx_rates.closing_rate?.toFixed(6)}
                  </p>
                  <p style={{ margin: '0.25rem 0' }}>
                    Average Rate: {result.fx_rates.average_rate?.toFixed(6)}
                  </p>
                </div>
              )}
              <p style={{ marginTop: '1rem', fontSize: '0.875rem' }}>
                The workbook has been generated and saved. You can download it from the history table.
              </p>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="modal-footer">
          {!success && (
            <>
              <button
                onClick={handleClose}
                className="btn btn-secondary"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                onClick={handleGenerate}
                className="btn btn-primary"
                disabled={
                  loading ||
                  !startDate ||
                  !endDate ||
                  (useManualRates && (!historicalRate || !closingRate || !averageRate))
                }
              >
                {loading ? (
                  <>
                    <span className="spinner" style={{ marginRight: '0.5rem' }}>⏳</span>
                    Generating...
                  </>
                ) : (
                  <>
                    <span style={{ marginRight: '0.5rem' }}>📊</span>
                    Generate FCTR
                  </>
                )}
              </button>
            </>
          )}
          {success && (
            <button
              onClick={handleClose}
              className="btn btn-primary"
            >
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default GenerateFCTRModal;
